import type {
  AdminStorePromotionalAdDestination,
  PromotionalAdDestinationSummary,
  PromotionalAdDestinationType,
  PublicStorePromotionalAdDestination,
  StorefrontBuilderPromotionalAdDestinationSummary,
  StorePromotionalAd,
  StorePromotionalAdDestination,
  StorePromotionalAdSafeExternalUrl,
} from '../types/promotional-ad';
import { isAllowedSocialUrl, normalizeSocialUrlInput } from './website-templates/external-url';

export interface AdminDestinationTarget {
  readonly categoryName?: string | null;
  readonly productName?: string | null;
  readonly isPublished?: boolean | null;
  readonly available?: number | null;
  readonly isAvailable?: boolean | null;
}

export interface PromotionalAdDestinationColumns {
  readonly destination_type: PromotionalAdDestinationType;
  readonly destination_category_id: string | null;
  readonly destination_product_id: string | null;
  readonly destination_external_url: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ALLOWED_EXTERNAL_HOSTS = ['facebook.com', 'instagram.com'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export function isAllowedPromotionalAdExternalHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return ALLOWED_EXTERNAL_HOSTS.some(
    (allowedHost) => normalized === allowedHost || normalized.endsWith(`.${allowedHost}`),
  );
}

/**
 * Epic #228 P5 (PD13): delegates to the one external-link rule
 * (`isAllowedSocialUrl`, mirrored by SQL `safe_store_promotional_ad_external_url`).
 * The scheme and host are lowercased (as the old `URL` serialization did) and
 * then checked; the URL is never re-serialized, so the value sent to the
 * server is the value it validates. Surrounding whitespace stays a rejection,
 * as before: the ads rule only tightens (hosts, ports, length).
 */
export function validateExternalPromotionalAdUrl(
  value: string | null | undefined,
): StorePromotionalAdSafeExternalUrl | null {
  if (typeof value !== 'string' || value !== value.trim()) return null;
  const normalized = normalizeSocialUrlInput(value);
  return isAllowedSocialUrl(normalized) ? (normalized as StorePromotionalAdSafeExternalUrl) : null;
}

export function normalizePromotionalAdDestination(value: unknown): StorePromotionalAdDestination {
  if (!isRecord(value) || typeof value.type !== 'string') return { type: 'none' };

  switch (value.type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return isUuid(value.categoryId)
        ? { type: 'category', categoryId: value.categoryId }
        : { type: 'none' };
    case 'product':
      return isUuid(value.productId)
        ? { type: 'product', productId: value.productId }
        : { type: 'none' };
    case 'external': {
      const url =
        typeof value.url === 'string' ? validateExternalPromotionalAdUrl(value.url) : null;
      return url ? { type: 'external', url } : { type: 'none' };
    }
    default:
      return { type: 'none' };
  }
}

export function buildPromotionalAdDestinationColumns(
  destination: StorePromotionalAdDestination,
): PromotionalAdDestinationColumns {
  switch (destination.type) {
    case 'sales':
      return {
        destination_type: 'sales',
        destination_category_id: null,
        destination_product_id: null,
        destination_external_url: null,
      };
    case 'category':
      return {
        destination_type: 'category',
        destination_category_id: destination.categoryId,
        destination_product_id: null,
        destination_external_url: null,
      };
    case 'product':
      return {
        destination_type: 'product',
        destination_category_id: null,
        destination_product_id: destination.productId,
        destination_external_url: null,
      };
    case 'external':
      return {
        destination_type: 'external',
        destination_category_id: null,
        destination_product_id: null,
        destination_external_url: destination.url,
      };
    default:
      return {
        destination_type: 'none',
        destination_category_id: null,
        destination_product_id: null,
        destination_external_url: null,
      };
  }
}

export function parseAdminPromotionalAdDestination(
  row: StorePromotionalAd,
  hydratedTarget?: AdminDestinationTarget | null,
): AdminStorePromotionalAdDestination {
  switch (row.destination_type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return row.destination_category_id
        ? {
            type: 'category',
            categoryId: row.destination_category_id,
            categoryName: hydratedTarget?.categoryName ?? null,
            isAvailable: hydratedTarget?.isAvailable ?? false,
          }
        : { type: 'none' };
    case 'product':
      return row.destination_product_id
        ? {
            type: 'product',
            productId: row.destination_product_id,
            productName: hydratedTarget?.productName ?? null,
            categoryName: hydratedTarget?.categoryName ?? null,
            isPublished: hydratedTarget?.isPublished ?? null,
            available: hydratedTarget?.available ?? null,
            isAvailable: hydratedTarget?.isAvailable ?? false,
          }
        : { type: 'none' };
    case 'external': {
      const url = validateExternalPromotionalAdUrl(row.destination_external_url);
      return url ? { type: 'external', url } : { type: 'none' };
    }
    default:
      return { type: 'none' };
  }
}

export function parsePublicPromotionalAdDestination(
  value: unknown,
): PublicStorePromotionalAdDestination {
  if (!isRecord(value) || typeof value.type !== 'string') return { type: 'none' };

  switch (value.type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return {
        type: 'category',
        categoryId: value.categoryId === null || isUuid(value.categoryId) ? value.categoryId : null,
      };
    case 'product':
      return {
        type: 'product',
        productId: value.productId === null || isUuid(value.productId) ? value.productId : null,
      };
    case 'external': {
      const url =
        typeof value.url === 'string' ? validateExternalPromotionalAdUrl(value.url) : null;
      return url ? { type: 'external', url } : { type: 'none' };
    }
    default:
      return { type: 'none' };
  }
}

export function summarizePromotionalAdDestination(
  destination: AdminStorePromotionalAdDestination,
): PromotionalAdDestinationSummary {
  switch (destination.type) {
    case 'sales':
      return { key: 'admin.promotionalAds.destination.summary.sales' };
    case 'category':
      return destination.isAvailable
        ? {
            key: 'admin.promotionalAds.destination.summary.category',
            value: destination.categoryName ?? undefined,
          }
        : { key: 'admin.promotionalAds.destination.summary.unavailable' };
    case 'product':
      return destination.isAvailable
        ? {
            key: 'admin.promotionalAds.destination.summary.product',
            value: destination.productName ?? undefined,
          }
        : { key: 'admin.promotionalAds.destination.summary.unavailable' };
    case 'external': {
      const hostname = new URL(destination.url).hostname;
      return { key: 'admin.promotionalAds.destination.summary.external', value: hostname };
    }
    default:
      return { key: 'admin.promotionalAds.destination.summary.noLink' };
  }
}

export function summarizePublicPromotionalAdDestinationForBuilder(
  value: unknown,
): StorefrontBuilderPromotionalAdDestinationSummary {
  const destination = parsePublicPromotionalAdDestination(value);
  switch (destination.type) {
    case 'sales':
      return { key: 'admin.promotionalAds.destination.summary.sales' };
    case 'category':
      return destination.categoryId
        ? { key: 'admin.promotionalAds.destination.summary.category' }
        : { key: 'admin.promotionalAds.destination.summary.unavailable' };
    case 'product':
      return destination.productId
        ? { key: 'admin.promotionalAds.destination.summary.product' }
        : { key: 'admin.promotionalAds.destination.summary.unavailable' };
    case 'external':
      return { key: 'admin.promotionalAds.destination.summary.external' };
    default:
      return { key: 'admin.promotionalAds.destination.summary.noLink' };
  }
}
